import React, { useState, useEffect } from "react";
import {
  Alert,
  Button,
  Modal,
  Loader,
  Grid,
  Center,
  Tooltip,
} from "@mantine/core";
import { DateRangePicker } from "@mantine/dates";
import { Link } from "react-router-dom";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";

import { auth, db } from "../../firebase";
import { getWeekOldDate } from "../listings/hooks";
import { fetchReportDownloadLinkCombined } from "./hooks/generate-report";
import DownloadAlert from "./downloadAlert";
import { DynamicFilterBuilder } from "../reports/DynamicFilterBuilder";
import { ReportConfig, CompanyCredits } from "@captiva/shared-types";

export interface ReportRunnerProps {
  reportConfig: ReportConfig & { id: string; name?: string };
  companyId: string;
  triggerButtonText?: string;
}

export function ReportRunner({
  reportConfig,
  companyId,
  triggerButtonText = "Generate Report",
}: ReportRunnerProps) {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedDateRange, setSelectedDateRange] = useState<[Date | null, Date | null]>([
    getWeekOldDate(1),
    new Date(),
  ]);
  const [loading, setLoading] = useState<boolean>(false);
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>();
  const [downloadModalOpen, setDownloadModalOpen] = useState<boolean>(false);
  const [creditDoc, setCreditDoc] = useState<CompanyCredits | undefined>();
  const [fetchedCredits, setFetchedCredits] = useState<boolean>(false);
  const [emptyReportWarning, setEmptyReportWarning] = useState<string | null>(null);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const [optionSets, setOptionSets] = useState<Record<string, string[]>>({});

  // Populate default filter values when reportConfig changes or modal opens
  useEffect(() => {
    if (reportConfig?.runtimeFilters && reportConfig.runtimeFilters.length > 0) {
      const defaults: Record<string, any> = {};
      reportConfig.runtimeFilters.forEach((f) => {
        defaults[f.id] =
          f.defaultValue !== undefined
            ? f.defaultValue
            : f.type === "dropdown"
            ? "ALL"
            : f.type === "number"
            ? f.min ?? 0
            : f.type === "toggle"
            ? false
            : "";
      });
      setFilterValues(defaults);
    } else {
      setFilterValues({});
    }
  }, [reportConfig, modalOpen]);

  // Resolve dynamic optionSets from flexFormSelectOptions when modal opens
  useEffect(() => {
    const hasDynamicOptionSets = reportConfig?.runtimeFilters?.some(
      (f) => f.optionsSource && f.optionsSource.startsWith("optionSet:")
    );
    if (!modalOpen || !companyId || !hasDynamicOptionSets) return;

    let cancelled = false;
    const loadOptionSets = async () => {
      try {
        const q = query(
          collection(db, "flexFormSelectOptions"),
          where("companyId", "==", companyId)
        );
        const snap = await getDocs(q);
        if (cancelled || !snap || !snap.docs) return;

        const map: Record<string, string[]> = {};
        const registerAliases = (rawKey: string | undefined | null, vals: string[]) => {
          if (!rawKey) return;
          const trimmed = rawKey.trim();
          if (!trimmed) return;
          const stripped = trimmed.replace(/\s*\(\d+\)$/, "").trim();
          const withoutPrefix = stripped.replace(/^os[-_]/i, "");
          const slugDash = withoutPrefix.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
          const slugUnderscore = withoutPrefix.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
          const slugCompact = withoutPrefix.toLowerCase().replace(/[^a-z0-9]+/g, "");

          [
            trimmed,
            trimmed.toLowerCase(),
            stripped,
            stripped.toLowerCase(),
            withoutPrefix,
            withoutPrefix.toLowerCase(),
            slugDash,
            slugUnderscore,
            slugCompact,
            `os-${slugDash}`,
          ].forEach((alias) => {
            if (alias) {
              map[alias] = vals;
            }
          });
        };

        snap.docs.forEach((docSnap) => {
          const data = docSnap.data() || {};
          const rawOptions = Array.isArray(data.options) ? data.options : [];
          const vals: string[] = rawOptions
            .map((opt: any) =>
              typeof opt === "string" ? opt : String(opt?.value ?? opt?.label ?? "")
            )
            .filter(Boolean);

          registerAliases(docSnap.id, vals);
          registerAliases(data.id, vals);
          registerAliases(data.name, vals);
          registerAliases(data.metadata?.originalOptionsSetId, vals);
        });

        setOptionSets(map);
      } catch (err) {
        console.error("Failed to load option sets for runtime filters:", err);
      }
    };

    loadOptionSets();
    return () => {
      cancelled = true;
    };
  }, [modalOpen, companyId, reportConfig]);

  // 1 day reports
  const handleDateRangeChange = (range: [Date | null, Date | null]) => {
    setSelectedDateRange(range);
  };

  useEffect(() => {
    if (!fetchedCredits && companyId) {
      const fetchCredits = async () => {
        setFetchedCredits(true);
        const docRef = doc(db, "credits", companyId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setCreditDoc(docSnap.data() as CompanyCredits);
        } else {
          console.error("No credit document");
        }
      };
      fetchCredits();
    }
  }, [companyId, fetchedCredits]);

  const handleGenerateReport = async () => {
    if (!selectedDateRange || !selectedDateRange[0]) return;

    // Use toLocaleDateString('en-CA') to ensure YYYY-MM-DD format in local timezone
    const startDate = selectedDateRange[0].toLocaleDateString("en-CA");
    // Handle single-day reports by defaulting endDate to startDate if not explicitly selected
    const endDate = (selectedDateRange[1] || selectedDateRange[0])!.toLocaleDateString("en-CA");
    const reportRenderType = reportConfig.renderingType;
    const reportName =
      (reportConfig.name || reportConfig.reportShortName) +
      `-${new Date().toLocaleDateString("en-CA")}`;

    setLoading(true);
    setEmptyReportWarning(null);

    const addlFieldMap =
      reportConfig.runtimeFilters && reportConfig.runtimeFilters.length > 0
        ? filterValues
        : Object.keys(filterValues).length > 0
        ? filterValues
        : null;

    try {
      const result = await fetchReportDownloadLinkCombined(
        reportConfig.id,
        startDate,
        endDate,
        reportRenderType,
        reportName,
        null,
        companyId,
        auth.currentUser?.uid || "",
        addlFieldMap,
        "flexReports" // reportConfigCollection
      );

      if (result?.empty) {
        setEmptyReportWarning(result.message);
      } else {
        setDownloadUrl(result);
        setDownloadModalOpen(true);
        setModalOpen(false);
      }
    } catch (error) {
      alert("Your report could not be generated. An error notification has been sent to the CaptivaData team for further investigation.");
    }

    setLoading(false);
  };

  return (
    <>
      <Tooltip
        label="Credit balance too low to generate reports"
        disabled={!(creditDoc && creditDoc.creditBalance !== undefined && creditDoc.creditBalance <= 0)}
        withArrow
      >
        <div style={{ display: "inline-block" }}>
          <Button
            color="cap-navy"
            size="sm"
            onClick={() => setModalOpen(true)}
            disabled={creditDoc && creditDoc.creditBalance !== undefined && creditDoc.creditBalance <= 0}
          >
            {triggerButtonText}
          </Button>
        </div>
      </Tooltip>
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`Generate ${reportConfig.name || reportConfig.reportShortName}`}
        size="md"
      >
        <DateRangePicker
          label="Choose a date range"
          placeholder="Report date range"
          amountOfMonths={2}
          clearable={false}
          value={selectedDateRange}
          onChange={handleDateRangeChange}
          allowSingleDateInRange
          style={{ marginBottom: "1em" }}
        />
        <DynamicFilterBuilder
          filters={reportConfig.runtimeFilters}
          values={filterValues}
          onChange={(id, val) =>
            setFilterValues((prev) => ({
              ...prev,
              [id]: val,
            }))
          }
          optionSets={optionSets}
        />
        {loading ? (
          <Center>
            <Loader size="lg" />
          </Center>
        ) : (
          <>
            {emptyReportWarning && (
              <Alert
                color="yellow"
                title="No data found"
                withCloseButton
                onClose={() => setEmptyReportWarning(null)}
                style={{ marginBottom: "1em" }}
              >
                {emptyReportWarning}
              </Alert>
            )}
            <Button
              color="cap-navy"
              size="md"
              fullWidth
              disabled={!selectedDateRange || (creditDoc && creditDoc.creditBalance !== undefined && creditDoc.creditBalance <= 0)}
              onClick={handleGenerateReport}
            >
              Generate Report
            </Button>
            {creditDoc && creditDoc.creditBalance !== undefined && creditDoc.creditBalance <= 0 && (
              <Grid
                gutter="sm"
                style={{ border: "1px solid #EAE0DA", borderRadius: "3px", marginTop: "1em" }}
              >
                <Grid.Col span={8}>
                  <Center style={{ height: "100%" }}>
                    Credit balance too low to generate reports.
                  </Center>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Link to="/dashboard/settings" style={{ textDecoration: "none" }}>
                    <Button color="red" size="sm" variant="outline">Top Up Credits</Button>
                  </Link>
                </Grid.Col>
              </Grid>
            )}
          </>
        )}
      </Modal>
      <DownloadAlert
        url={downloadUrl}
        modalOpen={downloadModalOpen}
        onModalClose={() => setDownloadModalOpen(false)}
      />
    </>
  );
}